import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabase';

const WebsiteContentContext = createContext();

export const useWebsiteContent = () => {
  const context = useContext(WebsiteContentContext);
  if (!context) {
    throw new Error('useWebsiteContent must be used within a WebsiteContentProvider');
  }
  return context;
};

export const WebsiteContentProvider = ({ children }) => {
  const [contentBlocks, setContentBlocks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('content_blocks')
        .select('*')
        .order('page', { ascending: true })
        .order('section', { ascending: true })
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error loading content:', error);
        return;
      }

      setContentBlocks(data || []);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateContent = async (id, content) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('content_blocks')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      setContentBlocks(contentBlocks.map((block) => (block.id === id ? data : block)));
      setLoading(false);
      return { success: true, data };
    } catch (error) {
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  const getContentByPage = (page) => {
    return contentBlocks.filter((block) => block.page === page);
  };

  const getContentBySection = (page, section) => {
    return contentBlocks.filter((block) => block.page === page && block.section === section);
  };

  const value = {
    contentBlocks,
    loading,
    loadContent,
    updateContent,
    getContentByPage,
    getContentBySection,
  };

  return <WebsiteContentContext.Provider value={value}>{children}</WebsiteContentContext.Provider>;
};

